import { Head, Link } from '@inertiajs/react'
import { Badge, Box, Button, Card, Flex, Heading, Text } from '@radix-ui/themes'
import { Car, Instagram, Pencil } from 'lucide-react'
import ColorSwatch from '@/shared/cars/ColorSwatch'
import { CarPhotoGrid, CarSpecList } from '@/shared/cars/CarDetails'
import { DriverCarsShowEvent, DriverCarsShowProps } from '@/types/driver_cars'

const STATUS: Record<DriverCarsShowEvent['status'], { label: string; color: 'green' | 'blue' | 'gray' }> = {
  attended: { label: 'Was there', color: 'green' },
  going: { label: 'Going', color: 'blue' },
  unconfirmed: { label: 'Said they were going', color: 'gray' },
}

/** "Where's this car been?" — a car's public page. */
export default function DriverCarShow({ car, events, edit_path }: DriverCarsShowProps) {
  const heading = [car.year, car.make, car.model].filter(Boolean).join(' ')
  const title = `${car.color_family ? `${car.color_family.label} ` : ''}${heading}`

  return (
    <>
      <Head title={`${car.driver_name}'s ${heading}`} />
      <div className="flex flex-col gap-6 px-4 pt-6 pb-12 lg:px-48">
        <Flex justify="between" align="start" gap="4" wrap="wrap">
          <Box>
            <Flex align="center" gap="2">
              {car.color_family && <ColorSwatch color={car.color_family} size={22} />}
              <Heading size="8">{title}</Heading>
            </Flex>
            <Text as="p" size="3" color="gray" mt="1">
              {car.driver_name}
            </Text>
            {car.review_status === 'pending' && <Badge color="amber" mt="2">Pending review — only you can see this page</Badge>}
            {car.review_status === 'rejected' && (
              <Badge color="red" mt="2">Not approved — only you can see this page</Badge>
            )}
          </Box>
          <Flex gap="2">
            {car.instagram && (
              <Button asChild variant="soft" color="gray">
                <a href={`https://instagram.com/${car.instagram}`} target="_blank" rel="noopener noreferrer">
                  <Instagram size={16} />@{car.instagram}
                </a>
              </Button>
            )}
            {edit_path && (
              <Button asChild variant="soft">
                <Link href={edit_path}>
                  <Pencil size={14} /> Edit
                </Link>
              </Button>
            )}
          </Flex>
        </Flex>

        {car.photos.length > 0 ? (
          <CarPhotoGrid photos={car.photos} driverName={car.driver_name} height={320} />
        ) : (
          <Flex align="center" justify="center" style={{ height: 220, borderRadius: 8, background: 'var(--gray-3)' }}>
            <Car size={56} color="var(--gray-8)" />
          </Flex>
        )}

        <CarSpecList specs={car.specs} />

        <Box>
          <Heading size="6" mb="3">
            Where it's been
          </Heading>
          {events.length === 0 ? (
            <Text color="gray">Not at any events yet.</Text>
          ) : (
            <Flex direction="column" gap="2">
              {events.map((event) => (
                <Card key={event.id} asChild>
                  <Link href={`/events/${event.id}`} style={{ textDecoration: 'none' }}>
                    <Flex justify="between" align="center" gap="3">
                      <Box>
                        <Text as="p" weight="medium">{event.name}</Text>
                        <Text as="p" size="2" color="gray">
                          {event.track_name} · {event.date}
                        </Text>
                      </Box>
                      <Badge color={STATUS[event.status].color} variant="soft">
                        {STATUS[event.status].label}
                      </Badge>
                    </Flex>
                  </Link>
                </Card>
              ))}
            </Flex>
          )}
          <Text as="p" size="1" color="gray" mt="3">
            Self-reported.
          </Text>
        </Box>
      </div>
    </>
  )
}
